'use client';

import { fmtNum } from '@slims/shared/calculations';

import { Row, Text } from '../../atoms';
import { BodySkeleton, PanelCard, PanelCardSkeleton } from '../../molecules';

export interface EnergyOverviewTotals {
	/** Energia prodotta: misura dei contatori M2 delle sezioni d'impianto (RO CACER §2.2.3), kWh. */
	prodotta: number | null;
	/** Energia prelevata dalla rete dai POD di consumo, kWh. */
	prelevata: number | null;
	/** Energia immessa in rete, kWh. */
	immessa: number | null;
	/** Energia condivisa nel perimetro, kWh. */
	condivisa: number | null;
	/** Quota condivisa entro la soglia di incentivabilità, kWh. */
	incentivabile: number | null;
	/** Quota condivisa oltre la soglia di incentivabilità, kWh. */
	eccedentaria: number | null;
}

const TILES: { key: keyof EnergyOverviewTotals; label: string }[] = [
	{ key: 'prodotta', label: 'Prodotta' },
	{ key: 'prelevata', label: 'Prelevata' },
	{ key: 'immessa', label: 'Immessa' },
	{ key: 'condivisa', label: 'Condivisa' },
	{ key: 'incentivabile', label: 'Incentivabile' },
	{ key: 'eccedentaria', label: 'Eccedentaria' },
];

/**
 * Panoramica energetica: i sei numeri del periodo e la ripartizione
 * dell'energia immessa.
 *
 * La barra sotto i riquadri non è decorativa: è l'unico punto in cui si vede
 * che l'immessa si divide in tre destini — non condivisa, condivisa
 * incentivabile, eccedentaria oltre la soglia. Leggere i tre numeri in colonna
 * non dice che sono parti dello stesso totale.
 */
export function EnergyOverviewCard({ totals }: { totals: EnergyOverviewTotals }) {
	const immessa = totals.immessa ?? 0;
	const incentivabile = totals.incentivabile ?? 0;
	const eccedentaria = totals.eccedentaria ?? 0;
	const condivisa = Math.min(immessa, totals.condivisa ?? incentivabile + eccedentaria);
	// Le tre quote della condivisa sono disgiunte: l'eccedentaria sta dentro la
	// condivisa, non accanto (INB-342). Quel che resta della condivisa è la
	// quota degli impianti preesistenti, esclusa dalla tariffa premio.
	const nonCondivisa = Math.max(0, immessa - condivisa);
	// Sotto il kWh è arrotondamento fra condivisa e incentivabile, non una quota.
	const resto = condivisa - incentivabile - eccedentaria;
	const nonIncentivabile = resto >= 1 ? resto : 0;
	const pct = (v: number) => (immessa > 0 ? Math.round((v / immessa) * 100) : 0);

	return (
		<PanelCard
			spaced
			title="Panoramica energetica"
			aside={
				<Text as="span" variant="micro" tone="muted">
					valori del periodo selezionato
				</Text>
			}
		>
			<div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
				{TILES.map((t) => (
					<span key={t.key} className="rounded-lg border border-border bg-card px-3 py-2.5">
						<Text
							as="span"
							variant="micro"
							tone="muted"
							className="mb-1.5 block uppercase tracking-wide"
						>
							{t.label}
						</Text>
						<Text as="span" variant="dataMd" tone="purple" className="block">
							{totals[t.key] != null ? fmtNum(Math.round(totals[t.key] as number)) : '-'}
						</Text>
						<Text as="span" variant="micro" tone="muted" className="block">
							kWh
						</Text>
					</span>
				))}
			</div>

			<Text variant="caption" tone="muted" weight="bold" className="mb-1.5">
				Ripartizione dell'energia immessa
			</Text>
			{immessa > 0 ? (
				<>
					<div className="flex h-8 overflow-hidden rounded-lg bg-border">
						<FlowSegment
							flex={nonCondivisa}
							color="var(--color-accent)"
							label={`Non condivisa · ${pct(nonCondivisa)}%`}
						/>
						<FlowSegment
							flex={nonIncentivabile}
							color="var(--color-text-muted)"
							label={`Condivisa non incentivabile · ${pct(nonIncentivabile)}%`}
						/>
						<FlowSegment
							flex={incentivabile}
							color="var(--color-accent-green)"
							label={`Incentivabile · ${pct(incentivabile)}%`}
						/>
						<FlowSegment
							flex={eccedentaria}
							color="var(--color-warn)"
							label={`Eccedentaria · ${pct(eccedentaria)}%`}
						/>
					</div>
					<Row gap="lg" className="mt-2.5 flex-wrap">
						<LegendDot color="var(--color-accent)" label="Immessa non condivisa" />
						{nonIncentivabile > 0 && (
							<LegendDot
								color="var(--color-text-muted)"
								label="Condivisa non incentivabile (impianti preesistenti)"
							/>
						)}
						<LegendDot color="var(--color-accent-green)" label="Incentivabile (entro la soglia)" />
						<LegendDot color="var(--color-warn)" label="Eccedentaria (oltre la soglia)" />
					</Row>
				</>
			) : (
				<Text variant="caption" tone="muted">
					Ripartizione disponibile quando c'è energia immessa misurata nel periodo.
				</Text>
			)}
		</PanelCard>
	);
}
EnergyOverviewCard.displayName = 'EnergyOverviewCard';

/**
 * Skeleton di {@link EnergyOverviewCard}: le sei tile + la riga di
 * ripartizione. Quest'ultima nella card reale non ha un'altezza fissa — è una
 * barra a flusso quando c'è energia immessa, una singola riga di testo
 * altrimenti — e finché il dato non arriva non si sa quale delle due sarà.
 * Lo skeleton replica il caso più piccolo (riga di testo): se poi arriva la
 * barra, la card *cresce*, che si legge come contenuto che compare; il
 * contrario — uno skeleton alto quanto la barra che poi *si restringe* a una
 * riga — si legge come le card che si avvicinano tra loro.
 */
export function EnergyOverviewSkeleton() {
	return (
		<PanelCardSkeleton title="Panoramica energetica" spaced>
			<div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
				{TILES.map((t) => (
					<span key={t.key} className="rounded-lg border border-border bg-card px-3 py-2.5">
						<BodySkeleton className="mb-1.5 h-2.5 w-12" />
						<BodySkeleton className="mb-1 h-4 w-14" />
						<BodySkeleton className="h-2.5 w-8" />
					</span>
				))}
			</div>
			<BodySkeleton className="mb-1.5 h-3 w-40" />
			<BodySkeleton className="h-3 w-72" />
		</PanelCardSkeleton>
	);
}
EnergyOverviewSkeleton.displayName = 'EnergyOverviewSkeleton';

function FlowSegment({ flex, color, label }: { flex: number; color: string; label: string }) {
	if (flex <= 0) return null;
	return (
		<span
			className="flex min-w-0 items-center justify-center overflow-hidden whitespace-nowrap px-1.5 font-mono text-[11px] font-extrabold text-white"
			style={{ flex, background: color }}
		>
			{label}
		</span>
	);
}

function LegendDot({ color, label }: { color: string; label: string }) {
	return (
		<Text as="span" variant="caption" tone="muted" className="inline-flex items-center gap-1.5">
			<span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color }} />
			{label}
		</Text>
	);
}
